import { Location } from "@angular/common";
import { AfterContentInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, ViewContainerRef } from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { LoggingService } from "@core/services";
import { deepFind, getValueFromPath, indexOrFirst, indexOrLast } from "@modules/common";
import { LookupListService } from "@modules/common/services/lookup-list.service";
import { NgbModal, NgbNav, NgbNavChangeEvent } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import {
  AddressDto,
  addressName,
  BaseCalendarDto,
  codeOnly,
  ContactDto,
  CustomerAddressDto,
  CustomerContactDto,
  CustomerMasterDto,
  JobDto,
  JobJournalDto,
  LookupObjectDto,
  MachineMasterDto,
  ModelMasterDto, newCustomerAddress,
  newCustomerContact,
  newCustomerMaster,
  newMachineMaster
} from "@shared/models";
import { DynamicModule } from "@shared/models/common/DynamicModule";
import { UUID } from "angular2-uuid";
import { combineLatest, forkJoin, from } from "rxjs";
import { delay, filter, switchMap, take, tap } from "rxjs/operators";
import { trackEvent } from "src/util";
import { AppQuery, LoginStatus } from "../../common/app.store";
import {
  AddressComponent,
  BaseFormDirective,
  ContactComponent,
  MachineMasterComponent
} from "../../common/components";
import {
  CodeListName,
  CustomerService,
  FileService,
  FilesUploader,
  JobService,
  LabelStatus, MachineService, ModelService, ProductSettingService, Progress, SnackBarService, UploadFile, WebLayoutService
} from "../../common/services";
import { ScheduleJobComponent } from "../schedule-job/schedule-job.component";

// Mutated sub-set of Sources
type SourceMethod = { code: string, sourceId: string, description: string};

/**
 * Map Your Tenant Specific Dynamic Modules here
 */
const tenantDynamicModules = {
  raleigh: async () => {
    const { RaleighModule } = await import(
      /* webpackChunkName: 'raleigh' */ "../../../tenants/raleigh/module/raleigh/raleigh.module"
    );
    return RaleighModule;
  },
  'raleigh-demo': async () => {
    const { RaleighModule } = await import(
      /* webpackChunkName: 'raleigh' */ "../../../tenants/raleigh/module/raleigh/raleigh.module"
    );
    return RaleighModule;
  },
  ozone: async () => {
    const { RaleighModule } = await import(
      /* webpackChunkName: 'raleigh' */ "../../../tenants/raleigh/module/raleigh/raleigh.module"
    );
    return RaleighModule;
  },
  'ozone-demo': async () => {
    const { RaleighModule } = await import(
      /* webpackChunkName: 'raleigh' */ "../../../tenants/raleigh/module/raleigh/raleigh.module"
    );
    return RaleighModule;
  }
};

@Component({
  templateUrl: "./create.component.html",
  styleUrls: ["./create.component.scss"],
  selector: "abi-jobs-create"
})
export class CreateComponent extends BaseFormDirective implements OnInit, AfterContentInit {
  @ViewChild(NgbNav) tabs: NgbNav;
  @ViewChild(ContactComponent) contactComponent: ContactComponent;
  @ViewChild(AddressComponent) addressComponent: AddressComponent;
  @ViewChild(MachineMasterComponent) machineComponent: MachineMasterComponent;
  @ViewChild("submitted") submittedModal;
  @ViewChild("navContent", {read: ElementRef}) navContent: ElementRef;
  @ViewChild("formElement", {read: ElementRef}) formElement: ElementRef;

  showDealerDetails = false;
  validateDealer = false;
  get sourceId(): AbstractControl {
    return this.form.get(["source", "sourceId"]);
  }
  customer: CustomerMasterDto;
  machines: MachineMasterDto[];
  job: JobDto;

  serviceTimeSlots: Map<string, { id: string, available: number}> = new Map();

  sourceForm: FormGroup;
  detailsForm: FormGroup;
  machineForm: FormGroup;
  activePage: string;
  pages: string;
  loggedIn = false;
  canSchedule = false;
  lastPageId: string;

  sources: { code: string; description: string }[] = [];
  firstPage = true;
  lastPage = false;
  confirmPage = false;
  submitting = false;
  jobAtBranch = false;
  hideBookingInfo = false;
  branchCalendar: BaseCalendarDto = null;

  fileUploadEnabled = false;// @todo: convert to 'getter' for productSetting OR 'fieldSettings'
  uploadingFiles = false;
  uploadProgress = 0;
  files: FilesUploader;
  fileInputAccept = FileService.FileInputAcceptString;

  filterCloseCodes: (item: LookupObjectDto, text: string) => boolean = null;
  filterComponentCodes: (item: LookupObjectDto, text: string) => boolean = null;
  filterDiagnosticCodes: (item: LookupObjectDto, text: string) => boolean = null;
  filterFaultCodes: (item: LookupObjectDto, text: string) => boolean = null;

  validationMessages = {
    sourceId: {
      required: "Job.SourceIDRequired",
    },
    consent: {
      required: "Job.ConsentRequired",
    },
    typeId: {
      required: "Job.TypeIDRequired",
    },
    faultCodeId: {
      required: "Job.FaultCodeIDRequired",
    },
    faultDescription: {
      required: "Job.FaultDescriptionRequired",
    },
    faultImages: {
      exceeded: (control: AbstractControl) => {
        const maxFaultImages = this.productSettings.numericValue("JobCreateMaxFaultImages") || 5;
        return `Max (${maxFaultImages}) number of Files exceeded`;
      },
    },
    ...MachineMasterComponent.validationMessages,
  };

  faultCodeComponent: any; // should be: DynamicFormComponent;
  initialLoad: boolean;

  constructor(
    protected layoutService: WebLayoutService,
    private route: ActivatedRoute,
    fb: FormBuilder,
    private productSettings: ProductSettingService,
    private lookups: LookupListService,
    private jobService: JobService,
    private machineService: MachineService,
    private customerService: CustomerService,
    private appQuery: AppQuery,
    private dialog: MatDialog,
    private modal: NgbModal,
    private location: Location,
    private changeRef: ChangeDetectorRef,
    translate: TranslateService,
    private fileService: FileService,
    private loggingService: LoggingService,
    private snackBar: SnackBarService,
    private modelService: ModelService
  ) {
    super(layoutService, fb);
    this.initialLoad = true;
    this.appQuery.$loginStatus.subscribe(stat => {
      this.loggedIn = stat === LoginStatus.True;
    });


    // A Special Param to enable the Job at Branch selection options
    this.jobAtBranch = productSettings.booleanValue(
      "JobAtBranch"
    );
    this.hideBookingInfo = productSettings.booleanValue(
      "JobCreateHideBookingInfo"
    );
    this.fileUploadEnabled = productSettings.booleanValue( // todo: MIGRATE TO CUSTOM LAYOUT THINGS
      "JobCreateFileUploader" // Implied Public File Upload
    );
  }

  // Post Field Settings Load
  applyValidation() {
    // Here we assume that if we are shoing these fields, that they MUST be required...
    this.detailsForm.get("diagnosticCodeId").setValidators(this.showField('diagnosticCodeId', false) ? Validators.required : []);
    this.detailsForm.get("typeId").setValidators(this.showField('typeId', true) ? Validators.required : []);
  }

  ngAfterContentInit(): void {
    this.loadLookups()
    .pipe(
      take(1),
      switchMap(() => this.getFieldSettings()),
    )
    .subscribe((fs) => {
      this.fieldSettings = fs;
      if (this.sources.length === 1) {
        this.sourceForm.controls.sourceId.patchValue(this.sources[0].code, { emitEvent: true });
        this.activePage = 'contact';
      } else {
        this.activePage = 'source';
      }

      this.applyValidation();

      this.initialLoad = false;
      this.productSettings.getLanguageLoadedObs()
      .pipe(this.notDisposed())
      .pipe(filter(loaded => loaded))
      .pipe(take(1))
      .subscribe(l => {
        this.jobService.loadCategories();//.subscribe(); @todo: why not subscribe?
        this.jobService.loadCodesConfig().subscribe();
        this.canSchedule = this.appQuery.hasRole("CC");
        this.route.paramMap
          .pipe(this.notDisposed())
          .pipe(
            take(1),
            switchMap((params: ParamMap) => {
              return from(
                this.configureAll(
                  params.get("customerId"),
                  params.get("contactId"),
                  params.get("addressId"),
                  params.get("lastname"),
                  params.get("firstname"),
                  params.get("telephone"),
                  params.get("machineId"),
                  params.get("field1"),
                  params.get("field2"),
                  params.get("field3"),
                  params.get("field4"),
                  params.get("field5"),
                  params.get("typeId"),
                  params.get("diagnosticCodeId"),
                  params.get("faultCodeId"),
                  params.get("faultDescription"),
                  // passed in params via external routes
                  params.get("modelId"),
                  params.get("serialNumber"),
                  params.get("machineField1"),
                  params.get("machineField2"),
                  params.get("machineField3"),
                  params.get("machineField4"),
                  params.get("machineField5"),
                )
              );
            })
          )
          .subscribe((result) => {
            // Triggers 'machineId' changed event - requires the View to be fully rendered (MachineMasterComponent)
              this.job = result.job;
              this.customer = result.customer;
              this.machines = result.customer.machines;
              this.currentDealer = this.job.dealerContact;// init dealer contact (customer contact is inited by the for value changes event)
              this.form.patchValue({
                // dealerContact: result.job.dealerContact,// note the Dto structure and form structure for a Contact a different
                contactId: result.customer.contacts[result.ids[0]],
                addressId: result.customer.addresses[result.ids[1]],
                details: {
                  typeId: result.job.typeId,
                  customerRef: result.job.customerRef,
                  field1: result.job.field1,
                  field2: result.job.field2,
                  field3: result.job.field3,
                  field4: result.job.field4,
                  field5: result.job.field5,

                  diagnosticCodeId: result.job.diagnosticCodeId,
                  faultCodeId: result.job.faultCodeId,
                  faultDescription: result.job.faultDescription,
                },
                machineId: result.customer.machines[result.ids[2]]?.machineId || this.machines[0].machineId,
                source: {
                  consent: result.customer?.consent || "",
                }
              });

              this.setJobAtBranchValues();

              this.initDynamicComponents().then(() => {
                this.formValueChanged();

                // Code SMELL: the whole Create Page loading process should be re-fined
                this.changeRef.detectChanges(); // machine changes require change detection to re-run (for MachineMasterComponent, and Others)
                this.machineChanged(this.form.value.machineId);
              });

              // Scroll to 'TOP' of the page
              this.formElement.nativeElement.scrollIntoView({ behavior: "smooth", block: "start" });
          });
      });
    });
  }

  setJobAtBranchValues() {
    if(this.jobAtBranch) {
      // TODO: need to select next available date here
      const branchId = this.productSettings.stringValue("JobCreateVisitBranchId");
      this.detailsForm.controls.serviceBranchId.setValue(branchId);
      const serviceDate = new Date().add(1, "day");
      this.detailsForm.controls.serviceDate.setValue(serviceDate);
      const timeSlotTime = this.productSettings.stringValue("JobCreateVisitTimeSlotTime");// format: "08:00:00"
      if(timeSlotTime){
        const timeSlot = serviceDate.format("yyyy-MM-dd") + "T" + timeSlotTime;// 2024-07-26T08:00:00
        this.detailsForm.controls.serviceTimeSlot.setValue(timeSlot);
      }
    }
  }

  get contactForm(): FormGroup {
    return this.form.get("contact") as FormGroup;
  }

  public jobTypeFilter(item: LookupObjectDto): boolean {
    return (
      (this.loggedIn && item.order < 100) ||
      (!this.loggedIn && item.order >= 100) ||
      item.order === 0
    );
  }

  private loadLookups() {
    // We need to know if we have Sources so that we can Display the Source Tab
    return combineLatest([this.appQuery.$loginStatus, this.lookups.lookupList("CodeJobSource")])
    .pipe(
      this.notDisposed(),
      tap(
        ([loginStatus, list]) => {
          // Old Mode
          this.sources = list.values
          // 'order' 100 and above are intended for 'Public' side
          // 'order' 1 and above are intended for 'Private' side
          .filter((f) => f.active && (f.order >= 100 || (loginStatus === LoginStatus.True && f.order > 0)))
        }
      )
    );

    // AddressComponent.setFormData(this.customer.addresses[1], this.addressForm);
    // ContactComponent.setFormData(this.customer.contacts[1], this.contactForm);
    // MachineMasterComponent.setFormData(this.customer.machines[0], this.machineForm);
  }

  ngOnInit() {
    super.ngOnInit();
  }

  schedulingRoles = ["CC"];
  setupRoles() {
    this.schedulingRoles = this.productSettings.arrayValue("JobSchedulingRoles", this.schedulingRoles);
    this.canSchedule = this.appQuery.hasRoles(this.schedulingRoles);
  }

  get layoutName(): string {
    return 'JobCreate';
  }

  /**
   * Currently only Raleigh has Dynamic components
   */
  initDynamicComponents(): Promise<void> {
    const tenant = this.appQuery.tenant2;
    if(this.fieldSettings?.faultCodeComponent){

      if(tenantDynamicModules[tenant]){
        return tenantDynamicModules[tenant]()
        .then((dynamicModule) => { // new tenant modules can be added (loaded dynamically here)
          this.faultCodeComponent = (dynamicModule as DynamicModule).components[this.fieldSettings.faultCodeComponent as string];
          // note: can add new custom components here...
        });
      } else {
        return Promise.resolve();
      }
    } else {
      return Promise.resolve();
    }
  }

  getJobAtBranchFormFields() {


    return {
      serviceBranchId: ["", Validators.required],
      serviceDate: ["", Validators.required],
      serviceTimeSlot: ["", Validators.required]
    };
  }

  subscribeJobAtBranchFields() {
    // Ensure we have a relevant calendar for the Selected Branch
    this.detailsForm.controls.serviceBranchId.valueChanges
    .pipe(
      filter((dealerBranch) => dealerBranch)
    )
    .pipe(this.notDisposed())
    .subscribe(
      (dealerBranch) => {
        const baseCalendarId = dealerBranch.baseCalendarID;
        this.lookups.lookupListEx<BaseCalendarDto>("CodeBaseCalendar")
        .subscribe(llCals => {
          this.branchCalendar = llCals.item(baseCalendarId);
        });
      }
    );
    // Get TimeSlots for Date/Branch combo
    combineLatest(
      [
        this.detailsForm.controls.serviceBranchId.valueChanges,
        this.detailsForm.controls.serviceDate.valueChanges
      ]
    )
    .pipe(this.notDisposed())
    .pipe(
      filter(([dealerBranch, date]) => dealerBranch && date)
    )
    .subscribe(
      ([dealerBranch, date]) => {
          this.jobService.getAvailableTimeSlots(codeOnly(dealerBranch), date.format("yyyy-MM-dd")).subscribe((timeSlots) => {
            this.serviceTimeSlots = new Map(Object.entries(timeSlots));
          });
      }
    );
    // Ensure a Visit object is created and booked dates are assigned to Job Dto
    combineLatest(
      [
        this.detailsForm.controls.serviceBranchId.valueChanges,
        this.detailsForm.controls.serviceDate.valueChanges,
        this.detailsForm.controls.serviceTimeSlot.valueChanges
      ]
    )
    .pipe(
      this.notDisposed(),
      filter(([dealerBranch, date]) => dealerBranch && date),
    )
    .subscribe(([dealerBranch, date, data]) => {
      // add a job visits entry with statusId of booked
      this.job.visits.splice(0, this.job.visits.length);
      this.job.visits.push(this.jobService.visitFactory(data, "BOOKED", codeOnly(dealerBranch)));
      this.job.booked = data;// "Timeslot (dateTtime)"
      // console.log("Job Visits", this.job.visits);
    });
  }

  faultImagesValidator() {
    const maxFaultImages = this.productSettings.numericValue("JobCreateMaxFaultImages") || 5;
    return (formArray: FormArray) => {
      const valid = formArray?.value ? formArray?.value?.length <= maxFaultImages : true;
      return valid ? null : { exceeded: true};
    };
  }

  createForm() {
    // Code Smell - will we use these settings for each field as we need them?
    // or should we make a more explicit tenant configuration? web layouts
    const required = this.productSettings.stringValue(
      "CustomerCustomStringValue1"
    )
      ? Validators.required
      : null;

    this.sourceForm = this.formBuilder.group({
      sourceId: ["", Validators.required],
      consent: ["", required],
    });
    this.machineForm = MachineMasterComponent.createFormGroup(this.formBuilder);

    const faultLbl = this.productSettings.label("JobFaultCodeIDAlias", this.loggedIn ? LabelStatus.Ignore : LabelStatus.Zero);
    const reqFaultType = !!faultLbl ? Validators.required : null;

    this.detailsForm = this.formBuilder.group({ // Service Form
      // Code Smell: we are controlling a lot of fields via this setting which makes it difficult to be flexible around this
      // we must implement a more flexible WebLayout form settings feature
      diagnosticCodeId: "",
      typeId: "",
      faultCodeId: ["", reqFaultType],
      faultDescription: ["", Validators.required],
      customerRef: "",
      // file upload
      ...(this.fileUploadEnabled ? { faultImages: [null, this.faultImagesValidator()] } : {}),
      // JobAtBranch
      ...(this.jobAtBranch ? this.getJobAtBranchFormFields() : {}),
      field1: "",
      field2: "",
      field3: "",
      field4: "",
      field5: "",
    });

    if (this.jobAtBranch) {
      this.subscribeJobAtBranchFields();
    }

    this.form = this.formBuilder.group({
      source: this.sourceForm,
      contact: ContactComponent.createFormGroup(this.formBuilder),
      address: AddressComponent.createFormGroup(this.formBuilder),
      machineMaster: this.machineForm,
      details: this.detailsForm, // Service
      dealerContact: ContactComponent.createFormGroup(this.formBuilder),
      contactId: "",
      addressId: "",
      machineId: "",
    });
    this.form.controls.contactId.valueChanges
      .pipe(this.notDisposed())
      .subscribe((data) => this.contactChanged(data));
    this.form.controls.addressId.valueChanges
      .pipe(this.notDisposed())
      .subscribe((data) => this.addressChanged(data));
    this.form.controls.machineId.valueChanges
      .pipe(this.notDisposed())
      .subscribe((data) => this.machineChanged(data));
    this.form.controls.source.valueChanges
      .pipe(this.notDisposed(), delay(100))
      .subscribe((data) => this.sourceChanged(data));

      super.createForm();
    // this.validate(true);
  }

  private updateAllowedCodes(model: ModelMasterDto) {
    if (this.jobService.updateAllowedCodes(model)) {
      this.filterCloseCodes = this.jobService.filterCloseCodes.bind(this.jobService);
      this.filterComponentCodes = this.jobService.filterComponentCodes.bind(this.jobService);
      this.filterDiagnosticCodes = this.jobService.filterDiagnosticCodes.bind(this.jobService);
      this.filterFaultCodes = this.jobService.filterFaultCodes.bind(this.jobService);
    }
  }

  getQueryPathExtras(listName: CodeListName): string {
    switch(listName) {
      case "CodeJobDiagnostic":
        return this.getFilterListValueForList(listName);
      case "CodeJobFault":
        return this.getFilterListValueForList(listName);
      case "CodeJobType":
        return this.getFilterListValueForList(listName);
      default:
        return "";
        // check the service which dependency uses this key
    }
  }

  getFilterListValueForList(listName: CodeListName){
    let filterValueListName = "";
    filterValueListName = this.jobService.codeGroupConfig?.[listName] || "";
    switch(filterValueListName) {
      case "CodeJobDiagnostic":
        return this.detailsForm.get("diagnosticCodeId").value;
      case "CodeJobFault":
        return this.detailsForm.get("faultCodeId").value;
      case "CodeJobType":
        return this.detailsForm.get("typeId").value;
      case "ModelMaster":
        return this.form.get("machineMaster").value.modelId;
        case "CodeMachineCategory_1":
          return this.form.get("machineMaster").value.categories[0];
        case "CodeMachineCategory_2":
          return this.form.get("machineMaster").value.categories[1];
        case "CodeMachineCategory_3":
          return this.form.get("machineMaster").value.categories[2];
        case "CodeMachineCategory_4":
          return this.form.get("machineMaster").value.categories[3];
        case "CodeMachineCategory_5":
          return this.form.get("machineMaster").value.categories[4];
        default:
          return "";
        // check the service which dependency uses this key
    }
  }

  private configureAll(
    customerId: string,
    contactId: string,
    addressId: string,
    lastname: string,
    firstname: string,
    telephone: string,
    machineId: string,
    field1: string,
    field2: string,
    field3: string,
    field4: string,
    field5: string,
    jobType: string,
    diagnosticCodeId: string,
    faultCodeId: string,
    faultDescription: string,
    // these are generally ignored if there is a valid Machine ID
    modelId: string,
    serialNumber: string,
    machineField1: string,
    machineField2: string,
    machineField3: string,
    machineField4: string,
    machineField5: string
  ): Promise<{ job: JobDto; customer: CustomerMasterDto; ids: number[] }> {
    let promise: Promise<CustomerMasterDto>;
    let user = this.appQuery.username;
    if (this.appQuery.roles.includes("TECH") && this.appQuery.defaultResource && this.appQuery.defaultResource !== "*")
    {
      user = this.appQuery.defaultResource + " " + user;
    }
    const job = this.jobService.jobFactory(user);
    job.field1 = field1;
    job.field2 = field2;
    job.field3 = field3;
    job.field4 = field4;
    job.field5 = field5;
    job.typeId = jobType;
    job.diagnosticCodeId = diagnosticCodeId;
    job.faultCodeId = faultCodeId;
    job.faultDescription = faultDescription;

    const newAddress = newCustomerAddress();
    newAddress.addressId = "NEW";
    const newContact = newCustomerContact();
    newContact.firstName = firstname;
    newContact.lastName = lastname;
    newContact.mobile = telephone;
    newContact.contactId = "NEW";
    const newMachine = job.machine;

    if(modelId || serialNumber || machineField1 || machineField2 || machineField3 || machineField4 || machineField5) {
      if(modelId){
        this.modelService.queryModels(null, modelId).subscribe(models => {
          // this.job.model = models[0];
          newMachine.model = models[0];
          newMachine.modelId = modelId;
          newMachine.serialNumber = serialNumber;
          newMachine.fields = [machineField1, machineField2, machineField3, machineField4, machineField5];
          newMachine.name = models[0].description;
          // will apply categories here from 'model' object
          MachineMasterComponent.setFormData(
            newMachine,
            this.form.get("machineMaster") as FormGroup,
            this.productSettings
          );
        });
      } else {
        newMachine.modelId = modelId;
        newMachine.serialNumber = serialNumber;
        newMachine.fields = [machineField1, machineField2, machineField3, machineField4, machineField5];
      }
    }

    // Job Type wasnt specified explicitly
    if(!jobType) {
      // grab from setting
      const defaultJobType = this.productSettings.stringValue("DefaultJobType");
      job.typeId = defaultJobType;
    }

    if (customerId) {
      promise = this.customerService
        .getCustomer(customerId)
        .pipe(this.notDisposed())
        .pipe(take(1))
        .toPromise()
        .then((customer) => {
          // Copy Customer Reference into Job.CustomerReference
          if(this.productSettings.booleanValue("JobCreateCustomerRefFromCustomer"))
            job.customerRef = customer.ourReference;

            customer.machines = customer.machines.filter(m => m.statusId === "A");
          // Grab Customer Machines into List?
          for (const machine of customer.machines) {
            // Add Model to Machine
            machine.model = customer.models.find(
              (m) => m.code === machine.modelId
            );
          }
          job.customerId = customerId;
          return customer;
        });
    } else {
      promise = Promise.resolve(newCustomerMaster()).then((customer) => {
        job.customerId = UUID.UUID().toString();
        return customer;
      });
    }

    return promise.then((customer) => {
      customer.addresses.splice(0, 0, newAddress);
      customer.contacts.splice(0, 0, newContact);
      if (!this.productSettings.booleanValue("MachineBlockNew") || !customer.contracts)
        customer.machines.splice(0, 0, newMachine);
      return {
        job,
        customer,
        ids: [
          indexOrLast(customer.contacts, (c) => c.contactId === contactId),
          indexOrLast(
            customer.addresses,
            (a) => a.addressId === addressId
          ),
          // if machine ID is set...
          indexOrFirst(
            customer.machines,
            (a) => a.machineId === machineId
          ),
          0,
        ],
      };
    });
  }

  public contactName(contact: CustomerContactDto): string {
    return contact.contactId === "NEW"
      ? "Contact.NewContact"
      : contact.companyName ||
          (contact.firstName || " ") + " " + (contact.lastName || "");
  }

  public addressName(address: CustomerAddressDto): string {
    const addressNameFormat = this.productSettings.stringValue("JobCreateAddressNameFormat");
    return addressName(address, addressNameFormat);
  }

  public machineName(machine: MachineMasterDto): string {
    const machineNameFormat = this.productSettings.stringValue("JobCreateMachineNameFormat");
    if(machine.machineId === "NEW") return "Machine.NewMachine";
    if(machineNameFormat) {
      const formattedValue = getValueFromPath(machine, machineNameFormat);
      if(formattedValue)
        return formattedValue;
    }
    return `${machine.modelId} ${machine.name}`;
  }

  private canChange(activeId: string, nextId: string): boolean {
    let group: AbstractControl;
    switch (activeId) {
      case "source":
        group = this.sourceForm;
        break;
      case "contact":
        group = this.form.get("contact");
        break;
      case "address":
        group = this.form.get("address");
        break;
      case "machine":
        group = this.machineForm;
        break;
      case "details":
        group = this.detailsForm;
        break;
      case "dealerContact":
        group = this.form.get("dealerContact");
        break;
    }
    const tabs = this.tabs.items.toArray();
    const oldId = tabs.findIndex((t) => t.id === activeId);
    const newId = tabs.findIndex((t) => t.id === nextId);
    if (newId > oldId && group) {
      if (group instanceof FormGroup) {
        //console.log("Validating Group", group, activeId, nextId);
        this.validateGroup(group, "", true);
        //group.markAsDirty();
        return group.disabled || (group.enabled && group.valid);
      }
      group.markAsDirty();
      return group.disabled || (group.enabled && group.valid);
    }
    return true;
  }

  public beforeChange($event: NgbNavChangeEvent) {
    if (!this.canChange($event.activeId, $event.nextId))
      $event.preventDefault();
  }

  public afterChange(newId: any) {
    if (this.tabs) {
      this.firstPage = this.tabs.items.first.id === newId;
      this.lastPage = this.tabs.items.last.id === newId || newId === this.lastPageId;
      // this.navContent.nativeElement.focus();
    }
  }

  schedule() {
    const modalRef = this.modal.open(ScheduleJobComponent, { size: "xl" });
    modalRef.componentInstance.job = this.job;
    trackEvent("Job Logging", "schedule_job", this.isPublic ? "public" : "internal");
  }

  /**
   * This is horrendous - we need to refactor this to be more dynamic
   */
  sourceChanged(data?: any) {
    const sourceId = data?.sourceId;
    const hasSetting = () => {
      return !!this.productSettings.stringValue('JobNewDealerTabVisibilitySources');
    };
    const sourceInSetting = (source: string) => {
      const dealerVisibilitySetting = this.productSettings.stringValue('JobNewDealerTabVisibilitySources');
      return (dealerVisibilitySetting && dealerVisibilitySetting.split(',').some(i => i === sourceId));
    };

    this.showDealerDetails = sourceInSetting(sourceId)
    || sourceId === "DEALWEB"
    || sourceId === "AGENTWEB";

    // Last Page ID
    this.lastPageId = this.showDealerDetails ? "dealerContact" : "details";

    // FORM Disability settings
    const dealerContact = this.form.get("dealerContact");
    if (
      (
        (hasSetting() && !sourceInSetting(sourceId))
        || (!hasSetting() && !["DEALWEB", "AGENTWEB"].includes(sourceId))
      )
      && dealerContact.enabled) {
        dealerContact.disable();
    } else if (
      (
        (hasSetting() && sourceInSetting(sourceId))
        || (!hasSetting() && ["DEALWEB", "AGENTWEB"].includes(sourceId))
      )
      && dealerContact.disabled) {
        dealerContact.enable();
    }

    // VALIDATE IF DEALER SOURCE
    this.validateDealer = sourceId === "DEALWEB";
  }

  finish() {
    if (!this.confirmPage) {
      this.confirmPage = this.confirmJob();
    } else if (!this.submitting) {
      this.submitJob();
    }
    return false;
  }

  previousPage() {
    const items = this.tabs.items.toArray();
    let cId: number;
    if (this.lastPage && this.confirmPage)
      cId = items.findIndex((t) => t.id === this.lastPageId) + 1;
    else
      cId = items.findIndex((t) => t.id === this.tabs.activeId);
    this.lastPage = this.confirmPage;
    this.confirmPage = false;
    if (cId > 0) {
      const nextId = items[cId - 1].id;
      if (this.canChange(this.tabs.activeId, nextId)) this.tabs.select(nextId);
    }
    return false;
  }

  nextPage() {
    const items = this.tabs.items.toArray();
    const cId = items.findIndex((t) => t.id === this.tabs.activeId);
    if (cId < items.length - 1) {
      const nextId = items[cId + 1].id;
      if (this.canChange(this.tabs.activeId, nextId)) this.tabs.select(nextId);
    }
    return false;
  }

  cancel() {
    this.location.back();
    return false;
  }

  private updateAddressName(newName: string, address: AddressDto) {
    address.name = newName;
    this.changeRef.markForCheck();
    this.changeRef.detectChanges();
  }

  currentDealer: ContactDto;

  currentContact: CustomerContactDto;
  contactChanged(contact: CustomerContactDto) {
    this.currentContact = contact;
  }

  currentAddress: CustomerAddressDto;
  addressChanged(address: CustomerAddressDto) {
    this.currentAddress = address;
  }

  // TODO: Review this method
  machineChanged(machineId: string) {
    if (this.job && this.machineComponent) {
      const newMachine = this.findMachine(machineId);
      this.job.machine = newMachine;
      this.machineComponent.setFormData(newMachine, this.machineForm);// call directly into the component here as it's not a ValueAccessor

      if(this.productSettings.booleanValue("MachineUnlock")) {
        // Prevent Immediate Editing of Existing Machines - hide behind a button
        // if(newMachine && newMachine.machineId === "NEW") {
        //   this.machineForm.enable();
        // } else {
        //   this.machineForm.disable();
        // }
      }

      if (newMachine.model) {
        this.updateAllowedCodes(newMachine.model);
      }
    }
  }

  // new journals must always get the 'isNew' flag set
  addMachineJournalEntry(journal: JobJournalDto) {
    this.job.machine.journals = this.job.machine.journals || [];
    this.job.machine.journals.push({ ...journal, isNew: true });
  }

  allowModelEdit(){
    this.machineForm.enable();
  }

  findMachine(machineId: string): MachineMasterDto {
    return this.machines.find(machine => machine.machineId === machineId);
  }

  get faultImages(): FormArray {
    return this.detailsForm.get("faultImages") as FormArray;
  }

  // Only removes index from faultImages FormArray
  removeIndex(fileList: UploadFile[], i: number) {
    (this.detailsForm.controls.faultImages as FormArray).removeAt(i);
  }

  get jobAttachments(): UploadFile[] {
    return this.detailsForm.controls.faultImages?.value || [];
  }

  get machineAttachments(): UploadFile[] {
    return this.machineForm.controls.attachment?.value || [];
  }

  // TODO: should return UploadFile[]
  get allFiles(): UploadFile[] {
    const filesArray = this.machineAttachments.concat(this.jobAttachments);
    return filesArray;
  }
  Array = Array;

  private confirmJob(): boolean {
    try {
      this.validate(true);
      if (this.form.valid) {
        const values = this.form.getRawValue();
        const job = this.job;
        job.contact = this.currentContact;
        job.address = this.currentAddress;
        MachineMasterComponent.getFormData(
          job.machine,
          this.form.controls.machineMaster as FormGroup
        );
        job.dealerContact = this.currentDealer;
        job.sourceId = values.source.sourceId;
        job.consent = values.source.consent;
        job.faultDescription = values.details.faultDescription;
        job.faultCodeId = codeOnly(values.details.faultCodeId);
        job.typeId = codeOnly(values.details.typeId);
        job.diagnosticCodeId = codeOnly(values.details.diagnosticCodeId);
        job.customerRef = values.details.customerRef;
        job.field1 = codeOnly(values.details.field1);
        job.field2 = codeOnly(values.details.field2);
        job.field3 = codeOnly(values.details.field3);
        job.field4 = codeOnly(values.details.field4);
        job.field5 = codeOnly(values.details.field5);
        job.machine.journals = job.machine.journals?.filter(j => j.isNew) || [];
        return true;
      }
      // console.log(this.form.errors)
    } catch (e) {
      // console.log(e)
    }
    return false;
  }

  get loading() {
    return this.submitting || this.uploadingFiles || this.initialLoad;
  }

  showMachineUnlock() {
    return this.form.getRawValue().machineId !== 'NEW' && this.productSettings.booleanValue('MachineUnlock');
  }

  // TODO: either use a 'custom' redirect text in a dialog or just the plain old dialog
  openFinishedDialog() {
    // Show the Confirmation Modal
    const ref = this.modal.open(this.submittedModal, { centered: true, size: "sm", backdrop: "static"});
    ref.closed.pipe(this.notDisposed()).subscribe((closeValue) => {
        // an undefine/null closevalue - just means - no click on a button
        // Raleigh Specific Code
        // Sometimes we'd like to redirect elsewhere other than back to the Job view (when logged out)
        if (closeValue && this.productSettings.hasSetting("JobPublicRedirect")) {
          this.submitting = true;
          if ( window.location !== window.parent.location ) {
            window.parent.location.href = this.productSettings.stringValue("JobPublicRedirect");
          } else {
            window.location.href = this.productSettings.stringValue("JobPublicRedirect");
          }
        } else {
          this.location.back();
        }
    });
  }

  submitJob() {
    this.submitting = true;
    try {
      this.jobService
        .newJob(this.job, this.isPublic)
        .toPromise()
        .then(
          (jobResponse: JobDto) => {
            this.submitting = false;
            this.job = jobResponse;
            if (this.shouldUploadFiles()){
              this.processFileUploads(jobResponse, () => {
                this.openFinishedDialog();
              });
            } else {
              this.openFinishedDialog();
            }
          },
          (e) => {
            this.loggingService.logException(`Job Creation Failed - ${e?.message || "Unknown Reason"}`);
            this.submitting = false;
          }
        );
    } catch (e) {
      this.loggingService.logException(`Job Creation Failed - ${e?.message || "Unknown Reason"}`);
      this.submitting = false;
    }
    trackEvent("Job Logging", "confirm_job", this.isPublic ? "public" : "internal");
  }

  shouldUploadFiles(){
    return this.machineAttachments?.length || this.jobAttachments?.length;
  }

  assembleUploads(jobId: string, machineId: string) {
    this.assembleMachineAttachmentPromises(machineId);
    this.assembleJobAttachmentPromises(jobId);
  }

  processFileUploads(job: JobDto, cb: () => void) {
    this.uploadingFiles = true;
    this.uploadProgress = 0;
    this.files = new FilesUploader(this.fileService, this.loggingService);
    this.files.getProgressSubject().subscribe(val => {
      this.uploadProgress = Math.floor(val);
    });
    this.assembleUploads(job.jobId, job.machine.machineId);
    this.files.executeSequentially()
      .finally(() => {
        this.uploadingFiles = false;
        cb();
      });
  }

  assembleMachineAttachmentPromises(machineId: string) {
    const machineAttachmentUploader = (file: File, descr: string, fileProgress: Progress) => {
      return this.machineService.uploadAttachment(machineId, file, descr, fileProgress, this.isPublic);
    };
    this.machineAttachments.forEach((item) => {
      this.files.addFile(item, machineAttachmentUploader);
    });
  }

  assembleJobAttachmentPromises(jobId: string) {
    const jobAttachmentUploader = (file, descr, fileProgress) => {
      return this.jobService.uploadAttachment(jobId, file, descr, fileProgress, this.isPublic);
    };
    this.jobAttachments.forEach((file) => {
      this.files.addFile(file, jobAttachmentUploader);// need description of file
    });
  }

  sourceDescription(): string {
    return this.sources.find(s => s.code === this.job.sourceId)?.description;
  }

  toggleDealerDetails() {
    const dealerContact = this.form.get("dealerContact");
    if (dealerContact.enabled) {
      dealerContact.disable();
    } else {
      dealerContact.enable();
    }
  }

  get isPublic(): boolean{
    return this.appQuery.loginStatus !== LoginStatus.True;
  }

  showField(field: string, defaultVisible: boolean = true) {
    if (this.fieldSettings?.[field]?.disabled === undefined) return defaultVisible; // default
    return this.fieldSettings?.[field]?.disabled === false ? true : false;
  }

  getString(settingId: string): string {
    return this.productSettings.stringValue(settingId);
  }
}
